import React from "react";
import cx from "classnames";
import { useHistory } from "react-router-dom";
import stylesV2 from "./NearbyOffice.module.scss";
import stylesV3 from "./NearbyOfficeV2.module.scss";
import { NearbyOfficeCard } from "./NearbyOfficeCard";
import { SectionDivider } from "components/SectionDivider";
import { PatientReturnStatus } from "types/patientReturnStatus";
import { ExamType } from "types/examType";
import { Office } from "types/office";
import { Spinner } from "components/Button/Spinner";
import locationpinnew from "images/locationpinnew.svg";
import { externalRedirect } from "utils/externalRedirect";
import * as paths from "features/routing/paths";
import { useSchedulingABVariant } from "../utils/useSchedulingABVariant";

interface NearbyOfficeProps {
  nearbyOffices: undefined | Office[];
  availabilitySlots: {
    [officeId: string]: {
      morning: number;
      afternoon: number;
      evening: number;
      total: number;
    };
  };
  loading?: boolean;
  isMobile: boolean;
  isEditForm?: false;
  onClick?: () => void;
  version?: number;
  isLoading?: boolean;
  examType: ExamType;
  patientReturnStatus: PatientReturnStatus;
  newDesign: string | null;
  screenSize?: number;
  useClassicStartLayout?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  showExpandedToggle?: boolean;
}

declare let REACT_APP_ENV: string | undefined;
export const NearbyOffice: React.FC<NearbyOfficeProps> = ({
  nearbyOffices,
  availabilitySlots,
  isMobile,
  isLoading,
  patientReturnStatus,
  examType,
  newDesign,
  screenSize,
  useClassicStartLayout = false,
  expanded,
  onExpandedChange,
  showExpandedToggle = true,
}) => {
  const history = useHistory();
  const [internalVisible, setInternalVisible] = React.useState(false);
  const visible = expanded === undefined ? internalVisible : expanded;
  const onClickVisible = () => {
    const nextVisible = !visible;
    setInternalVisible(nextVisible);
    onExpandedChange?.(nextVisible);
  };
  const onClickLocation = () => {
    const appEnv = typeof REACT_APP_ENV !== "undefined" ? REACT_APP_ENV : "";
    if (appEnv === "undefined") {
      externalRedirect(paths.LOCATIONS_PAGE_SEARCH_URL);
    } else {
      history.push(paths.offices());
    }
    return false;
  };

  const { isV3 } = useSchedulingABVariant();
  const styles =
    isV3 && !useClassicStartLayout ? stylesV3 : stylesV2;

  //const noNearbyHeading = <span>No Availability Nearby</span>;
  const headingText =
    nearbyOffices && nearbyOffices.length > 1 && isV3
      ? "Other Nearby Offices"
      : "Other Nearby Office";
  if (isLoading) {
    const nearbyLoadingHeading = <span>Fetching Nearby Offices</span>;
    return (
      <div className={styles.container}>
        <div className={styles.headerSection}>
          <Spinner className={styles.spinner} />
          <div className={cx(styles.heading, styles.loading)}>
            {nearbyLoadingHeading}
          </div>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        <div className={styles.mobilecontainer}>
          <div className={styles.contentSection}>
            {nearbyOffices && nearbyOffices.length === 0 && (
              <div className={cx(styles.nearbyContent, styles.closedContent)}>
                <div className={cx(styles.contentHeader, styles.noBackground)}>
                  <SectionDivider
                    visible={visible}
                    onClickVisible={onClickVisible}
                    version={2}
                    className={styles.centeredDivider}
                  >
                    <div className={styles.heading}>{headingText}</div>
                  </SectionDivider>
                </div>
                {visible && (
                  <>
                    <div className={styles.contentLeftNoAvail}>
                      <img src={locationpinnew} alt="todo" />
                    </div>
                    <div className={styles.contentRightNoAvail}>
                      We are unable to find the availability at a nearby
                      location. To view other offices go back to our{" "}
                      <span aria-hidden="true" onClick={onClickLocation}>
                        locations
                      </span>{" "}
                      page.
                    </div>
                  </>
                )}
              </div>
            )}
            {!visible && nearbyOffices && nearbyOffices.length > 0 && (
              <SectionDivider
                visible={visible}
                onClickVisible={onClickVisible}
                version={2}
                className={styles.centeredDivider}
              >
                <div className={styles.heading}>{headingText}</div>
              </SectionDivider>
            )}
            {nearbyOffices &&
              nearbyOffices.map((nearby) => {
                return (
                  <React.Fragment key={nearby.id}>
                    {visible && (
                      <NearbyOfficeCard
                        nearby={nearby}
                        availabilitySlots={availabilitySlots[nearby.id]}
                        patientReturnStatus={patientReturnStatus}
                        examType={examType}
                        newDesign={newDesign}
                        screenSize={screenSize}
                        useClassicStartLayout={useClassicStartLayout}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            {showExpandedToggle &&
              visible &&
              nearbyOffices &&
              nearbyOffices.length > 0 && (
                <SectionDivider
                  visible={visible}
                  onClickVisible={onClickVisible}
                  version={2}
                  className={styles.centeredDivider}
                >
                  <div className={styles.heading}>{headingText}</div>
                </SectionDivider>
              )}
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div className={styles.container}>
        <div className={styles.headerSection}>
          <div className={styles.heading}>{headingText}</div>
        </div>
        <div className={styles.contentSection}>
          {nearbyOffices &&
            nearbyOffices.map((nearby) => {
              return (
                <NearbyOfficeCard
                  key={nearby.id}
                  nearby={nearby}
                  availabilitySlots={availabilitySlots[nearby.id]}
                  patientReturnStatus={patientReturnStatus}
                  examType={examType}
                  newDesign={newDesign}
                  useClassicStartLayout={useClassicStartLayout}
                />
              );
            })}
          {!isLoading && nearbyOffices && nearbyOffices.length === 0 && (
            <div className={styles.nearbyContent}>
              <div className={styles.contentHeader}>
                <span className={styles.noAvailSpan}>
                  NO AVAILABILITY NEARBY
                </span>
              </div>
              <div className={styles.contentLeftNoAvail}>
                <img src={locationpinnew} alt="todo" />
              </div>
              <div className={styles.contentRightNoAvail}>
                We are unable to find the availability at a nearby location. To
                view other offices go back to our{" "}
                <span onClick={onClickLocation} aria-hidden="true">
                  locations
                </span>{" "}
                page.
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
